// ⚠️ FALLBACK ONLY — admin DB is the single source of truth.
//
// The ShippingCalculator now reads from /api/content.store_info.shipping_rates
// via the `useShippingRates` hook (which calls `adaptApiRatesToCalc`). This
// file is kept ONLY as a defensive offline fallback so the calculator never
// goes blank if the API is unreachable. The numbers below mirror the
// canonical seed in /app/backend/seed_data.py exactly; they are NOT the
// active source. To change prices, edit them in BOB Admin → Shipping & Fees.
// The admin edits flow to ALL public surfaces (country-page calculators,
// /shipping-rates embed, mobile app) automatically.

export const SERVICE_FEE_PCT = 12.5; // 12.5% of invoice value

export const RATES = {
  TR: {
    methods: [
      {
        id: "sea",
        name_en: "Sea Freight",
        name_ar: "شحن بحري",
        name_fr: "Fret maritime",
        transit_en: "10–14 days",
        transit_ar: "10–14 يوم",
        transit_fr: "10–14 jours",
        unit: "kg",
        categories: [
          { id: "clothes", price: 5.5, name_en: "Turkish brand clothing", name_ar: "ملابس ماركات تركية", name_fr: "Vêtements de marques turques" },
          { id: "intl", price: 5.5, name_en: "International brand clothing", name_ar: "ملابس ماركات عالمية", name_fr: "Vêtements de marques internationales" },
          { id: "medical", price: 5.5, name_en: "Medical products", name_ar: "منتجات طبية", name_fr: "Produits médicaux" },
          { id: "home", price: 4, name_en: "Home products", name_ar: "منتجات منزلية", name_fr: "Articles pour la maison" },
          { id: "toys", price: 4, name_en: "Toys / Stationery", name_ar: "ألعاب / قرطاسية", name_fr: "Jouets / Papeterie" },
        ],
      },
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "5–7 days",
        transit_ar: "5–7 أيام",
        transit_fr: "5–7 jours",
        unit: "kg",
        categories: [
          { id: "general", price: 12, name_en: "General goods", name_ar: "بضائع عامة", name_fr: "Marchandises générales" },
        ],
      },
    ],
  },
  CN: {
    methods: [
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "20–25 days",
        transit_ar: "20–25 يوم",
        transit_fr: "20–25 jours",
        unit: "kg",
        categories: [
          { id: "normal", price: 16, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "battery", price: 20, name_en: "Items with battery", name_ar: "منتجات تحتوي على بطارية", name_fr: "Articles avec batterie" },
        ],
      },
      {
        id: "sea",
        name_en: "Sea Freight",
        name_ar: "شحن بحري",
        name_fr: "Fret maritime",
        transit_en: "55–70 days",
        transit_ar: "55–70 يوم",
        transit_fr: "55–70 jours",
        unit: "cbm",
        categories: [
          { id: "normal", price: 400, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "clothes_shoes", price: 200, unit: "cbm",
            secondary_price: 5.5, secondary_unit: "kg",
            name_en: "Clothes & Shoes", name_ar: "ملابس وأحذية", name_fr: "Vêtements & Chaussures" },
        ],
      },
    ],
  },
  AE: {
    methods: [
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "20–25 days",
        transit_ar: "20–25 يوم",
        transit_fr: "20–25 jours",
        unit: "kg",
        categories: [
          { id: "normal", price: 14, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "cosmetics", price: 16, name_en: "Cosmetics", name_ar: "مستحضرات تجميل", name_fr: "Cosmétiques" },
          { id: "supplements", price: 25, name_en: "Supplements", name_ar: "مكمّلات غذائية", name_fr: "Compléments alimentaires" },
        ],
      },
    ],
  },
  US: {
    methods: [
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "20–25 days",
        transit_ar: "20–25 يوم",
        transit_fr: "20–25 jours",
        unit: "kg",
        categories: [
          { id: "normal", price: 28, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "perfumes", price: 30, name_en: "Perfumes & Cosmetics", name_ar: "العطورات ومواد التجميل", name_fr: "Parfums et cosmétiques" },
          { id: "supplements", price: 40, name_en: "Supplements", name_ar: "المكملات الغذائية", name_fr: "Compléments alimentaires" },
        ],
      },
    ],
  },
  GB: {
    methods: [
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "20–25 days",
        transit_ar: "20–25 يوم",
        transit_fr: "20–25 jours",
        unit: "kg",
        categories: [
          { id: "normal", price: 28, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "perfumes", price: 30, name_en: "Perfumes / Cosmetics", name_ar: "عطور / مستحضرات تجميل", name_fr: "Parfums / Cosmétiques" },
        ],
      },
    ],
  },
  FR: {
    methods: [
      {
        id: "air",
        name_en: "Air Freight",
        name_ar: "شحن جوي",
        name_fr: "Fret aérien",
        transit_en: "20–25 days",
        transit_ar: "20–25 يوم",
        transit_fr: "20–25 jours",
        unit: "kg",
        categories: [
          { id: "normal", price: 28, name_en: "Normal goods", name_ar: "بضائع عادية", name_fr: "Marchandises normales" },
          { id: "perfumes", price: 30, name_en: "Perfumes / Cosmetics", name_ar: "عطور / مستحضرات تجميل", name_fr: "Parfums / Cosmétiques" },
        ],
      },
    ],
  },
};
